import { ReactNode, useId } from 'react';
import { Link } from '@backstage/core-components';
import { useHomeStyles } from './homeStyles';

/**
 * A homepage shortcut. `card` is a launcher tile under the hero (no arrow; it
 * grows on rollover); `row` is a compact list row (icon, label, detail, arrow)
 * for the resources column. The link is named by its label; the detail is
 * announced as its description.
 */
export function ShortcutTile({
  to,
  label,
  icon,
  detail,
  external = false,
  variant = 'card',
}: {
  to: string;
  label: string;
  icon: ReactNode;
  detail?: string;
  external?: boolean;
  variant?: 'card' | 'row';
}) {
  const classes = useHomeStyles();
  const detailId = useId();
  return (
    <Link
      to={to}
      className={
        variant === 'card'
          ? `${classes.startCard} ${classes.launcherGrow}`
          : classes.toolRow
      }
      aria-label={label}
      aria-describedby={detail ? detailId : undefined}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <span className={classes.tileIcon} aria-hidden="true">
        {icon}
      </span>
      <span className={classes.tileText}>
        <span className={classes.tileLabel}>{label}</span>
        {detail && (
          <span id={detailId} className={classes.tileDetail}>
            {detail}
          </span>
        )}
      </span>
      {/* Launchers grow on rollover instead; rows keep their arrow. */}
      {variant === 'row' && (
        <span aria-hidden="true" data-arrow className={classes.tileArrow}>
          {external ? '↗' : '→'}
        </span>
      )}
    </Link>
  );
}
